import React, { useState, useCallback, Suspense, lazy, memo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ErrorBoundary } from 'react-error-boundary';
import { AnimatePresence, motion } from 'framer-motion';

import Header from './components/Header';
import Hero from './components/Hero';
import CapabilityExplorer from './components/CapabilityExplorer';
import ProofSection from './components/ProofSection';
import PartnersSection from './components/PartnersSection';
import StackOwned from './components/StackOwned';
import EngageSection from './components/EngageSection';
import FAQSection from './components/FAQSection';
import CloseCTA from './components/CloseCTA';
import ContactModal from './components/ContactModal';
import Footer from './components/Footer';
import SEO from './components/SEO';
import Loading from './components/Loading';
import ScrollProgress from './components/ScrollProgress';
import { ThemeProvider } from './theme/ThemeProvider';
import { ExploreProvider } from './context/ExploreContext';
import './App.css';

const Connect = lazy(() => import('./components/Connect'));
const PortfolioPage = lazy(() => import('./components/PortfolioPage'));
const PartnersPage = lazy(() => import('./components/PartnersPage'));
const BlogsPage = lazy(() => import('./components/BlogsPage'));
const BlogPost = lazy(() => import('./components/BlogPost'));

const ErrorFallback = ({ resetErrorBoundary }) => (
  <div className="error-boundary">
    <div className="error-content">
      <h2>Something went wrong</h2>
      <p>Reload the page to continue.</p>
      <button type="button" onClick={resetErrorBoundary} className="btn btn-primary">
        Try again
      </button>
    </div>
  </div>
);

const HomePage = memo(({ onContactClick }) => (
  <>
    <Hero onContactClick={onContactClick} />
    <CapabilityExplorer />
    <ProofSection />
    <PartnersSection />
    <StackOwned />
    <EngageSection />
    <FAQSection />
    <CloseCTA onContactClick={onContactClick} />
  </>
));

HomePage.displayName = 'HomePage';

const PageLoader = ({ label }) => (
  <Loading message={label} fullScreen />
);

function AnimatedRoutes({ onContactClick }) {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        className="page-transition"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -6 }}
        transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
      >
        <Routes location={location}>
          <Route path="/" element={<HomePage onContactClick={onContactClick} />} />
          <Route
            path="/connect"
            element={
              <Suspense fallback={<PageLoader label="Loading Connect…" />}>
                <Connect onContactClick={onContactClick} />
              </Suspense>
            }
          />
          <Route
            path="/portfolio"
            element={
              <Suspense fallback={<PageLoader label="Loading Work…" />}>
                <PortfolioPage onContactClick={onContactClick} />
              </Suspense>
            }
          />
          <Route
            path="/partners"
            element={
              <Suspense fallback={<PageLoader label="Loading Partners…" />}>
                <PartnersPage onContactClick={onContactClick} />
              </Suspense>
            }
          />
          <Route path="/solutions" element={<Navigate to="/partners" replace />} />
          <Route
            path="/blogs"
            element={
              <Suspense fallback={<PageLoader label="Loading Notes…" />}>
                <BlogsPage onContactClick={onContactClick} />
              </Suspense>
            }
          />
          <Route
            path="/blogs/:slug"
            element={
              <Suspense fallback={<PageLoader label="Loading article…" />}>
                <BlogPost />
              </Suspense>
            }
          />
          <Route
            path="*"
            element={
              <div className="not-found">
                <div className="container">
                  <p className="section-label">404</p>
                  <h1>Page not found</h1>
                  <p>That route does not exist.</p>
                  <Link to="/" className="btn btn-primary">
                    Go home
                  </Link>
                </div>
              </div>
            }
          />
        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

function App() {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  const handleContactClick = useCallback(() => {
    setIsContactModalOpen(true);
  }, []);

  const handleContactClose = useCallback(() => {
    setIsContactModalOpen(false);
  }, []);

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => window.location.reload()}
    >
      <HelmetProvider>
        <ThemeProvider>
          <ExploreProvider>
            <Router>
              <div className="App">
                <SEO />
                <ScrollProgress />
                <Header />
                <main className="main-content">
                  <AnimatedRoutes onContactClick={handleContactClick} />
                </main>
                <Footer />
                <ContactModal
                  isOpen={isContactModalOpen}
                  onClose={handleContactClose}
                />
              </div>
            </Router>
          </ExploreProvider>
        </ThemeProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default App;
